import { Box, useTheme } from "@mui/material";
import { TableCellProps } from "./types";

/**
 * Reusable table cell component with support for frozen columns and highlighting
 *
 * Z-Index hierarchy:
 * - Normal body cells: 1
 * - Normal header cells: 12 (matches TableHeader)
 * - Frozen body cells: 10
 * - Frozen header cells: 13 (above everything to prevent overlap during scroll)
 * - Highlight overlay: +1 to base cell z-index
 */
const TableCell = ({
  frozen,
  isHeader,
  align,
  isHighlighted,
  flex = 1,
  minWidth,
  children,
  sx,
  colors,
  categorycolor: categoryColor,
  ...props
}: TableCellProps) => {
  const theme = useTheme();
  const isHeaderCell = isHeader === true;
  // Frozen athlete column keeps responsive floor; other columns honor explicit minWidth (EN headers)
  const resolvedMinWidth = frozen
    ? { xs: "110px", sm: "200px" }
    : minWidth != null
      ? `${minWidth}px`
      : "auto";

  return (
    <Box
      {...props}
      sx={{
        padding: "8px 4px",
        display: "flex",
        alignItems: "center",
        justifyContent:
          align === "center"
            ? "center"
            : align === "right"
              ? "flex-end"
              : "flex-start",
        borderRight: "1px solid rgba(224, 224, 224, 1)",
        backgroundColor: isHeaderCell
          ? colors.primary
          : frozen
            ? categoryColor
              ? `${categoryColor.split(",").slice(0, 3).join(",")}, 1)`
              : theme.palette.background.paper || "white"
            : "inherit",
        color: isHeaderCell ? colors.primaryText : theme.palette.text.primary,
        fontWeight: isHeaderCell ? "bold" : "normal",
        position: frozen ? "sticky" : "relative",
        left: frozen ? 0 : "auto",
        zIndex: frozen ? (isHeaderCell ? 13 : 10) : isHeaderCell ? 12 : 1,
        boxShadow: frozen ? "2px 0 5px rgba(0,0,0,0.1)" : "none",
        // Stable sticky positioning without transforms
        ...(frozen && {
          // Force browser to create a new stacking context
          isolation: "isolate",
          // Prevent subpixel rendering issues
          WebkitBackfaceVisibility: "hidden",
          backfaceVisibility: "hidden",
          WebkitFontSmoothing: "antialiased",
          // Force integer pixel positioning
          WebkitTransformStyle: "preserve-3d",
          transformStyle: "preserve-3d",
        }),
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: isHeaderCell ? "normal" : "nowrap",
        boxSizing: "border-box",
        flex: `${flex} 1 0`,
        minWidth: resolvedMinWidth,
        "&::after": isHighlighted
          ? {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: frozen ? (isHeaderCell ? 14 : 14) : isHeaderCell ? 13 : 2,
              animation: "blink 1s infinite",
              borderTop: "2px solid red",
              borderBottom: "2px solid red",
              boxSizing: "border-box",
              pointerEvents: "none",
            }
          : {},
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default TableCell;
